import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Alert } from '@cfp/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EnergyLabelTypeDTO,
  EnergyLabelsItemDTO,
  ObjectTypeDTO,
  RegisteredOrCalculatedEnergyLabelDTO
} from 'csr-asset-app';

@Component({
  selector: 'cfp-report-building-energy-label-alert',
  templateUrl: 'report-building-energy-label-alert.html',
  styleUrls: ['report-building-energy-label-alert.scss'],
  imports: [Alert, TranslateModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportBuildingEnergyLabelAlert {
  public readonly energyLabels = input<EnergyLabelsItemDTO | undefined>(undefined);
  public readonly objectType = input<ObjectTypeDTO | undefined>(undefined);
  public readonly translationKeyPrefix = input('portfolioBuildingsRoadmapReport');
  /** When true, parking buildings show the generic parking warning instead of hiding the alert. */
  public readonly showParkingWarning = input(false);

  private readonly translateService = inject(TranslateService);

  protected readonly registeredEnergyLabel = computed((): RegisteredOrCalculatedEnergyLabelDTO | undefined => {
    const energyLabels = this.energyLabels();
    if (energyLabels?.currentEnergyLabel?.labelType === EnergyLabelTypeDTO.REGISTERED) {
      return energyLabels.currentEnergyLabel;
    }
    if (energyLabels?.clarificationLabel?.labelType === EnergyLabelTypeDTO.REGISTERED) {
      return energyLabels.clarificationLabel;
    }
    return undefined;
  });

  protected readonly currentEnergyLabel = computed(() => this.energyLabels()?.currentEnergyLabel);

  protected readonly alertEnergyLabel = computed(() => this.registeredEnergyLabel() ?? this.currentEnergyLabel());

  protected readonly parkingWarningHtml = computed(() => {
    if (!this.showParkingWarning() || this.objectType() !== ObjectTypeDTO.PARKING) {
      return undefined;
    }
    return this.translateService.instant('parkingText').split('.').join('<br>');
  });

  protected readonly warningKey = computed(() => {
    if (this.parkingWarningHtml()) {
      return undefined;
    }
    if (this.objectType() === ObjectTypeDTO.PARKING) {
      return undefined;
    }
    if (!this.alertEnergyLabel()?.label) {
      return undefined;
    }
    const registeredEnergyLabel = this.registeredEnergyLabel();
    if (registeredEnergyLabel) {
      const isExpired = registeredEnergyLabel.validUntil
        ? new Date(registeredEnergyLabel.validUntil) < new Date()
        : false;
      return `${this.translationKeyPrefix()}.buildings.alert.${isExpired ? 'expired' : 'registered'}`;
    }
    return `${this.translationKeyPrefix()}.buildings.alert.calculated`;
  });
}
